import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart, ShoppingCart, Zap, Minus, Plus, ChevronDown, ChevronLeft,
  Loader2, ShieldCheck, Truck, RotateCcw, Star, ZoomIn, Lock,
} from "lucide-react";
import { api, fmt, fmtDate, apiError, unitName, pluralUnit, fmtPerUnit, fmtQty } from "@/lib/api";
import { PrimeLockButton } from "@/components/ui/PrimeLockButton";
import PrimePromoBanner from "@/components/shop/PrimePromoBanner";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Stars, StockPill } from "@/components/shop/ProductCard";
import DeliveryChecker from "@/components/shop/DeliveryChecker";
import RecentlyViewed, { recordProductView } from "@/components/RecentlyViewed";
import { ShinyButton } from "@/components/ui/ShinyButton";

function Accordion({ title, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-line">
      <button
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between py-5 text-left"
      >
        <span className="font-display text-base font-medium tracking-tight text-1">{title}</span>
        <ChevronDown className={`h-4 w-4 text-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-sm leading-relaxed text-2">{children}</div>}
    </div>
  );
}

/* Renders admin-written descriptions the way they were typed: line breaks
   are preserved and **bold** markers render as real bold text. */
function RichDescription({ text }) {
  if (!text) return null;
  return (
    <div className="space-y-2" data-testid="product-description-rich">
      {text.split("\n").map((line, li) =>
        line.trim() === "" ? (
          <div key={li} className="h-2" />
        ) : (
          <p key={li}>
            {line.split(/(\*\*[^*]+\*\*)/g).map((seg, si) =>
              seg.startsWith("**") && seg.endsWith("**") ? (
                <strong key={si} className="font-bold text-1">
                  {seg.slice(2, -2)}
                </strong>
              ) : (
                <span key={si}>{seg}</span>
              )
            )}
          </p>
        )
      )}
    </div>
  );
}

export default function ProductPage() {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const primeChannel = searchParams.get("channel") === "prime";  // opened from the PRIME Store
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, toggleWishlist, wishlistIds, gstEnabled, primeActive } = useStore();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState({});
  const [selectedSku, setSelectedSku] = useState("");
  const [qty, setQty] = useState(1);
  const [qtyError, setQtyError] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const [reviews, setReviews] = useState(null);
  const [canReview, setCanReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", text: "" });
  const [editingReview, setEditingReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    api.get(`/shop/products/${productId}`)
      .then(({ data }) => {
        setProduct(data);
        const wantSku = searchParams.get("sku");
        const v0 = (wantSku && data.variants.find((v) => v.sku === wantSku)) || data.variants[0];
        setSelected(v0?.options || {});
        setSelectedSku(v0?.sku || "");
      })
      .catch(() => setNotFound(true));
    api.get(`/shop/products/${productId}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps -- sku read once on load; later param changes handled below

  // Deep-linked variant (?sku=) changed while already on this PDP — reselect.
  useEffect(() => {
    if (!product) return;
    const wantSku = searchParams.get("sku");
    if (!wantSku) return;
    const v = product.variants.find((vv) => vv.sku === wantSku);
    if (v && v.sku !== variant?.sku) {
      setSelected(v.options || {});
      setSelectedSku(v.sku);
      setActiveImg(0);
    }
  }, [searchParams, product]); // eslint-disable-line react-hooks/exhaustive-deps -- guard prevents loops

  useEffect(() => {
    if (user) {
      api.get(`/shop/products/${productId}/can-review`).then(({ data }) => setCanReview(data)).catch(() => {});
    } else {
      setCanReview(null);
    }
  }, [user, productId]);

  // Recently viewed: account sync for signed-in users, localStorage for guests.
  useEffect(() => {
    if (product?.id) recordProductView(product.id, user);
  }, [product?.id, user]); // eslint-disable-line

  const optionKeys = useMemo(
    () => (product ? [...new Set(product.variants.flatMap((v) => Object.keys(v.options || {})))] : []),
    [product]
  );

  const variant = useMemo(() => {
    if (!product) return null;
    if (optionKeys.length === 0) {
      return product.variants.find((v) => v.sku === selectedSku) || product.variants[0];
    }
    return (
      product.variants.find((v) => optionKeys.every((k) => (v.options || {})[k] === selected[k])) ||
      product.variants[0]
    );
  }, [product, optionKeys, selected, selectedSku]);

  // Two-way variant ↔ image sync. Every variant's images map back to its SKU.
  // Variant buttons select the variant (gallery switches to ITS images, primary
  // first); clicking a thumbnail linked to a different variant selects THAT
  // variant. Variant images are never treated as independent gallery images.
  const imageToSku = useMemo(() => {
    const map = {};
    (product?.variants || []).forEach((v) => {
      const imgs = v.images?.length ? v.images : (v.image ? [v.image] : []);
      imgs.forEach((u) => { if (!(u in map)) map[u] = v.sku; });
    });
    return map;
  }, [product]);

  const selectVariantByImage = (url, fallbackIdx) => {
    const sku = imageToSku[url];
    const target = sku ? product?.variants?.find((v) => v.sku === sku) : null;
    if (!target || sku === variant?.sku) {
      setActiveImg(fallbackIdx);  // same variant (or unlinked product image) — just change photo
      return;
    }
    if (optionKeys.length) {
      setSelected(Object.fromEntries(
        optionKeys.map((k) => [k, (target.options || {})[k]]).filter(([, val]) => val != null)
      ));
    } else {
      setSelectedSku(sku);
    }
    const gal = target.images?.length
      ? target.images
      : target.image
        ? [target.image, ...product.images.filter((i) => i !== target.image)]
        : product.images;
    const idx = gal.indexOf(url);
    setActiveImg(idx >= 0 ? idx : 0);
  };

  const wished = product && wishlistIds.includes(product.id);
  const out = variant?.status === "out_of_stock";
  const memberLocked = primeChannel && !primeActive;  // PRIME-channel purchase needs an active membership
  // Return-after-join: banner click → PRIME mode PDP → lock CTA → sign in/PRIME purchase → back here.
  const primePdpPath = product && variant ? `/shop/product/${product.id}?channel=prime&sku=${encodeURIComponent(variant.sku)}` : "";
  const primeJoinPath = `/prime?return=${encodeURIComponent(primePdpPath)}`;
  const primeJoinDest = user ? primeJoinPath : `/login?redirect=${encodeURIComponent(primeJoinPath)}`;
  const unit = product?.unit || "pc";
  const moq = variant?.min_order_qty || 1;
  const inc = moq;  // order steps are multiples of the MOQ (server enforces the same rule)
  const maxValidQty = variant ? Math.max(moq, Math.floor((variant.stock || moq) / moq) * moq) : moq;
  const qtyRuleMessage = (q) => {
    if (!Number.isFinite(q) || q <= 0) return `Enter at least ${moq} ${pluralUnit(unit, moq)}.`;
    if (q < moq) return `Minimum order is ${moq} ${pluralUnit(unit, moq)}.`;
    if (q % moq !== 0) {
      const lo = Math.floor(q / moq) * moq;
      const hi = lo + moq;
      return `Invalid quantity. This product can only be purchased in quantities of ${moq} ${pluralUnit(unit, moq)}. Please choose ${lo} or ${hi} ${pluralUnit(unit, hi)}.`;
    }
    return "";
  };

  useEffect(() => {
    if (variant) setQty(variant.min_order_qty || 1);
  }, [variant?.sku]); // eslint-disable-line react-hooks/exhaustive-deps -- reset MOQ only on variant change

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    try {
      await api.post(`/shop/products/${productId}/reviews`, reviewForm);
      toast.success("Review submitted — it will appear once our team approves it");
      setReviewForm({ rating: 5, title: "", text: "" });
      const [{ data: rev }, { data: prod }] = await Promise.all([
        api.get(`/shop/products/${productId}/reviews`),
        api.get(`/shop/products/${productId}`),
      ]);
      setReviews(rev);
      setProduct(prod);
      setCanReview({ can_review: false });
    } catch (err) {
      toast.error(apiError(err, "Couldn't submit review"));
    } finally {
      setReviewLoading(false);
    }
  };

  const startEditReview = (r) => {
    if (!r) return;
    setReviewForm({ rating: r.rating, title: r.title, text: r.text });
    setEditingReview(true);
  };

  const saveEditedReview = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    try {
      await api.put(`/shop/products/${productId}/reviews/mine`, reviewForm);
      toast.success("Review updated — it will reappear once approved");
      setEditingReview(false);
      const [{ data: rev }, { data: prod }] = await Promise.all([
        api.get(`/shop/products/${productId}/reviews`),
        api.get(`/shop/products/${productId}`),
      ]);
      setReviews(rev);
      setProduct(prod);
    } catch (err) {
      toast.error(apiError(err, "Couldn't update your review"));
    } finally {
      setReviewLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-semibold tracking-tight">Product not found.</h1>
        <p className="text-sm text-3">This product may have been removed or the link is incorrect.</p>
        <button onClick={() => navigate("/shop")} className="mt-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black hover:bg-amber-400" data-testid="product-not-found-back">
          Back to Shop
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pb-24 pt-28" data-testid="product-loading">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <div className="aspect-square animate-pulse rounded-2xl border border-line bg-surface" />
            <div className="mt-3 flex gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-20 w-20 animate-pulse rounded-xl border border-line bg-surface" />
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-3 w-28 animate-pulse rounded-full bg-hover" />
            <div className="h-9 w-3/4 animate-pulse rounded-lg bg-hover" />
            <div className="h-4 w-40 animate-pulse rounded-full bg-hover" />
            <div className="h-10 w-48 animate-pulse rounded-lg bg-hover" />
            <div className="h-12 w-full animate-pulse rounded-xl bg-hover" />
            <div className="h-12 w-full animate-pulse rounded-xl bg-hover" />
          </div>
        </div>
      </div>
    );
  }

  const images = variant?.images?.length
    ? variant.images  // variant has its own gallery — it takes over completely
    : variant?.image
      ? [variant.image, ...product.images.filter((i) => i !== variant.image)]
      : product.images;

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="product-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <nav data-testid="product-breadcrumb" className="mb-5 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-3">
          <button onClick={() => navigate("/")} className="transition-colors hover:text-amber-500">Home</button>
          <span>/</span>
          <button onClick={() => navigate("/shop")} className="transition-colors hover:text-amber-500">Shop</button>
          <span>/</span>
          <button onClick={() => navigate(`/shop?category=${encodeURIComponent(product.category)}`)} className="transition-colors hover:text-amber-500">{product.category}</button>
          <span>/</span>
          <span className="text-2 normal-case tracking-normal">{product.name}</span>
        </nav>
        <button
          data-testid="product-back-btn"
          onClick={() => navigate("/shop")}
          className="mb-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-3 transition-colors hover:text-amber-400"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Shop
        </button>

        <div className="grid gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <div
              className="relative aspect-square cursor-zoom-in overflow-hidden rounded-2xl border border-line bg-white shadow-card"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
              onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
              data-testid="product-main-image"
            >
              <img
                src={images[activeImg]}
                alt={product.name}
                className="h-full w-full object-contain p-6 transition-transform duration-200"
                style={zoom.on ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
              />
              {variant?.compare_at_price > variant?.price && (
                <span className="absolute left-4 top-4 rounded-md bg-amber-500 px-2.5 py-1 text-xs font-bold text-black">
                  {Math.round((1 - variant.price / variant.compare_at_price) * 100)}% OFF
                </span>
              )}
              <span className="pointer-events-none absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-3">
                <ZoomIn className="h-3 w-3" /> Hover to zoom
              </span>
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => selectVariantByImage(img, i)}
                    className={`h-20 w-20 overflow-hidden rounded-xl border bg-white p-1.5 transition-all ${
                      activeImg === i ? "border-amber-500 shadow-[0_0_0_3px_rgba(255,159,28,0.18)]" : "border-line opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-contain" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => navigate(`/shop?category=${encodeURIComponent(product.category)}`)}
                className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500 transition-colors hover:text-amber-400"
              >
                {product.category}
              </button>
              {product.brand_name && (
                <span className="rounded-full border border-line bg-hover px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-2" data-testid="product-brand">
                  {product.brand_name}
                </span>
              )}
            </div>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight text-1 sm:text-4xl" data-testid="product-name">
              {product.name}
            </h1>
            <button
              className="mt-3 flex items-center gap-2"
              onClick={() => document.getElementById("reviews-section")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="product-rating-link"
            >
              <Stars value={product.rating_avg} size={15} />
              <span className="text-sm font-semibold text-2">{product.rating_avg}</span>
              <span className="text-sm text-3">({product.rating_count} reviews)</span>
            </button>

            <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {(() => {
                // PRIME channel: the member price is the headline price for everyone;
                // regular channel: always the regular price (channel isolation).
                // Per-variant member price first, product-level price as fallback.
                const pp = (product.prime_prices && product.prime_prices[variant.sku]) ?? null;
                const primeValid = primeChannel && pp && pp > 0 && pp < variant.price;
                const shown = primeValid ? pp : variant.price;
                return (
                  <>
                    <span className="font-display text-4xl font-semibold tracking-tight text-[#A96C1E]" data-testid="product-price">
                      {fmt(shown)}<span className="text-xl text-3">/{unitName(unit)}</span>
                    </span>
                    {primeValid && (
                      <>
                        <span className="flex items-center gap-1.5 rounded-full border border-[#E4B66D]/50 bg-[#242521] px-2.5 py-1 text-[11px] font-extrabold text-[#E4B66D]" data-testid="prime-price-badge">
                          PRIME price · <span className="line-through opacity-70">{fmt(variant.price)}</span>
                        </span>
                        <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-600" data-testid="prime-pdp-saving">
                          Save {fmt(variant.price - pp)}
                        </span>
                      </>
                    )}
                  </>
                );
              })()}
              {variant.compare_at_price > variant.price && (
                <>
                  <span className="text-lg text-3 line-through">{fmt(variant.compare_at_price)}</span>
                  <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-500" data-testid="product-savings">
                    You save {fmt(Math.round((variant.compare_at_price - variant.price) * 100) / 100)}
                  </span>
                </>
              )}
            </div>

            {!primeChannel && product.prime_store && (product.prime_prices?.[variant.sku] ?? null) > 0 && product.prime_prices[variant.sku] < variant.price && !out && (
              <PrimePromoBanner
                primePrice={product.prime_prices[variant.sku]}
                regularPrice={variant.price}
                unit={unitName(unit)}
                hasVariants={product.variants.length > 1}
                onClick={() => navigate(primePdpPath)}
              />
            )}

            <p className="mt-1 text-xs text-3">{gstEnabled ? "Inclusive of GST · " : ""}SKU {variant.sku}</p>

            <div className="mt-4"><StockPill status={variant.status} /></div>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-3" data-testid="product-fulfilled-by">
              Stocked & shipped by PAQVERO
            </p>

            {optionKeys.length > 0 ? (
              optionKeys.map((key) => {
                const values = [...new Set(product.variants.map((v) => (v.options || {})[key]).filter(Boolean))];
                return (
                  <div key={key} className="mt-7">
                    <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-3">
                      {product.variant_attribute || (key === "pack" ? "Pack quantity" : key.charAt(0).toUpperCase() + key.slice(1))}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {values.map((val) => {
                        const avail = product.variants.some((v) => (v.options || {})[key] === val && v.stock > 0);
                        return (
                          <button
                            key={val}
                            data-testid={`variant-${key}-${val.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => { setSelected((s) => ({ ...s, [key]: val })); setActiveImg(0); }}
                            className={`rounded-xl border px-5 py-3 text-sm font-bold transition-all ${
                              selected[key] === val
                                ? "border-amber-500 bg-amber-500/10 text-amber-400"
                                : avail
                                  ? "border-line text-2 hoverable-strong"
                                  : "border-white/5 text-3 line-through"
                            }`}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : product.variants.length > 1 ? (
              <div className="mt-7">
                <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-3">Options</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.sku}
                      data-testid={`variant-${v.sku.toLowerCase()}`}
                      onClick={() => { setSelectedSku(v.sku); setActiveImg(0); }}
                      className={`rounded-xl border px-5 py-3 text-sm font-bold transition-all ${
                        variant.sku === v.sku
                          ? "border-amber-500 bg-amber-500/10 text-amber-400"
                          : "border-line text-2 hoverable-strong"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-7">
              <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-3">
                Quantity{moq > 1 || inc > 1 ? ` (${pluralUnit(unit, 2)})` : ""}
              </p>
              <div className="flex items-center rounded-xl border border-line">
                <button
                  data-testid="qty-minus"
                  onClick={() => { setQty((q) => Math.max(moq, q - moq)); setQtyError(""); }}
                  disabled={qty <= moq}
                  className="flex h-12 w-12 items-center justify-center text-2 hoverable-t1 disabled:opacity-30"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  data-testid="qty-input"
                  type="number"
                  min={moq}
                  step={moq}
                  max={maxValidQty}
                  value={qty}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setQty(Number.isFinite(v) ? v : 0);
                    setQtyError(qtyRuleMessage(v));
                  }}
                  onBlur={() => {
                    setQty((q) => {
                      if (!Number.isFinite(q) || q < moq) return moq;
                      const snapped = Math.round(q / moq) * moq;
                      return Math.min(maxValidQty, Math.max(moq, snapped));
                    });
                    setQtyError("");
                  }}
                  className="h-12 w-24 border-x border-line bg-transparent text-center text-sm font-bold text-1 focus:outline-none"
                />
                <button
                  data-testid="qty-plus"
                  onClick={() => { setQty((q) => Math.min(maxValidQty, q + moq)); setQtyError(""); }}
                  disabled={qty + moq > maxValidQty}
                  className="flex h-12 w-12 items-center justify-center text-2 hoverable-t1 disabled:opacity-30"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <span className="ml-3 text-xs font-semibold text-3">{pluralUnit(unit, qty)}</span>
                {variant.stock > 0 && variant.stock <= variant.low_stock_threshold && (
                  <span className="ml-4 text-xs font-bold text-amber-400">Only {variant.stock.toLocaleString("en-IN")} left</span>
                )}
              </div>
              {moq > 1 && (
                <p className="mt-2 text-[11px] text-3" data-testid="moq-hint">
                  Sold in quantities of {fmtQty(moq, unit)} — {Array.from({ length: 4 }, (_, k) => fmtQty(moq * (k + 1), unit)).join(", ")}…
                </p>
              )}
              {qtyError && (
                <p className="mt-2 text-[11px] font-semibold text-red-400" data-testid="qty-error">{qtyError}</p>
              )}
              <div className="mt-4 flex items-baseline justify-between rounded-xl border border-line bg-hover px-4 py-3">
                <span className="text-xs text-3" data-testid="product-qty-breakdown">
                  {fmtQty(qty, unit)} × {fmtPerUnit(variant.price, unit)}
                </span>
                <span className="font-display text-xl font-semibold text-amber-500" data-testid="product-total-price">
                  {fmt(Math.round(qty * (primeChannel && primeActive && product.prime_prices?.[variant.sku] != null && product.prime_prices[variant.sku] < variant.price ? product.prime_prices[variant.sku] : variant.price) * 100) / 100)}
                </span>
              </div>
            </div>

            <DeliveryChecker inStock={!out} />

            {primeChannel && !primeActive && (product.prime_prices?.[variant.sku] ?? null) && (
              <button
                onClick={() => navigate(primeJoinDest)}
                className="mt-4 flex w-full items-center justify-between rounded-xl border border-[#E4B66D]/45 bg-[#E4B66D]/[0.10] px-4 py-3 text-left transition-colors hover:bg-[#E4B66D]/[0.16]"
                data-testid="prime-pdp-teaser"
              >
                <span className="text-xs font-semibold text-[#242521]">
                  This is a PRIME Store listing — join PRIME to purchase at <span className="font-extrabold text-[#8A6A2F]">{fmt(product.prime_prices[variant.sku])}/{unit}</span>
                </span>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#A96C1E]">Join PRIME →</span>
              </button>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {memberLocked ? (
                <>
                  <PrimeLockButton
                    testid="product-add-to-cart"
                    top="Members only"
                    bottom="Join PRIME to Purchase"
                    onClick={() => navigate(primeJoinDest)}
                  />
                  <PrimeLockButton
                    testid="product-buy-now"
                    top="Members only"
                    bottom="Buy with PRIME"
                    onClick={() => navigate(primeJoinDest)}
                  />
                </>
              ) : (
                <>
              {out ? (
                <button
                  data-testid="product-add-to-cart"
                  disabled
                  className="flex h-12 flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-line text-sm font-bold text-3"
                >
                  <ShoppingCart className="h-[18px] w-[18px]" />
                  Currently Unavailable
                </button>
              ) : (
                <ShinyButton
                  variant="charcoal"
                  data-testid="product-add-to-cart"
                  onClick={() => {
                    const msg = qtyRuleMessage(qty);
                    if (msg) { setQtyError(msg); toast.error(msg); return; }
                    addItem(product.id, variant.sku, qty, { channel: primeChannel ? "prime" : "regular" });
                  }}
                  className="flex-1"
                  contentClassName="px-6 py-3.5 text-sm font-bold"
                >
                  <ShoppingCart className="h-[18px] w-[18px]" />
                  Add to Cart
                </ShinyButton>
              )}
              {out ? (
                <button
                  data-testid="product-buy-now"
                  disabled
                  className="flex h-12 flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-line text-sm font-bold text-3"
                >
                  <Zap className="h-[18px] w-[18px]" />
                  Buy Now
                </button>
              ) : (
                <ShinyButton
                  variant="charcoal"
                  data-testid="product-buy-now"
                  onClick={async () => {
                    const msg = qtyRuleMessage(qty);
                    if (msg) { setQtyError(msg); toast.error(msg); return; }
                    const ok = await addItem(product.id, variant.sku, qty, { silent: true, channel: primeChannel ? "prime" : "regular" });
                    if (ok) navigate("/checkout");
                  }}
                  className="flex-1"
                  contentClassName="px-6 py-3.5 text-sm font-bold"
                >
                  <Zap className="h-[18px] w-[18px]" />
                  Buy Now
                </ShinyButton>
              )}
                </>
              )}
              <button
                data-testid="product-wishlist-btn"
                onClick={() => toggleWishlist(product.id)}
                className={`flex h-12 w-12 items-center justify-center rounded-xl border transition-all ${
                  wished ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-strong text-2 hover:border-amber-500/50 hover:text-amber-400"
                }`}
                aria-label="Toggle wishlist"
              >
                <Heart className={`h-5 w-5 ${wished ? "fill-amber-400" : ""}`} />
              </button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { icon: Truck, label: "Ships in 24h" },
                { icon: ShieldCheck, label: "Secure checkout" },
                { icon: RotateCcw, label: "30-day returns" },
              ].map((f) => (
                <div key={f.label} className="flex flex-col items-center gap-1.5 rounded-xl border border-line bg-surface px-2 py-3.5 text-center shadow-card">
                  <f.icon className="h-[18px] w-[18px] text-amber-500" />
                  <span className="text-[11px] font-semibold text-2">{f.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          <div>
            <Accordion title="Description" defaultOpen testid="accordion-description">
              <RichDescription text={product.description} />
            </Accordion>
            {product.specs && Object.keys(product.specs).length > 0 && (
              <Accordion title="Specifications" testid="accordion-specs">
                <dl className="space-y-2.5">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-line pb-2.5">
                      <dt className="text-3">{k}</dt>
                      <dd className="text-right text-2">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Accordion>
            )}
            {product.whats_included && product.whats_included.length > 0 && (
              <Accordion title="What's Included" testid="accordion-included">
                <ul className="list-inside space-y-1.5">
                  {product.whats_included.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Accordion>
            )}
            <Accordion title="Shipping Information" testid="accordion-shipping">{product.shipping_info}</Accordion>
            <Accordion title="Return Policy" testid="accordion-returns">{product.return_policy}</Accordion>
          </div>

          <div id="reviews-section">
            <h3 className="font-display mb-5 text-xl font-medium tracking-tight" data-testid="reviews-heading">
              Reviews {reviews ? `(${reviews.count} verified)` : ""}
            </h3>
            {reviews && (
              <>
                <div className="mb-6 rounded-2xl border border-line bg-surface p-5">
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <p className="font-display text-4xl font-semibold">{product.rating_avg}</p>
                      <Stars value={product.rating_avg} />
                      <p className="mt-1 text-[11px] text-3">{product.rating_count} ratings</p>
                    </div>
                    <div className="flex-1 space-y-1.5">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = reviews.distribution?.[String(star)] || 0;
                        const pct = reviews.count ? (count / reviews.count) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center gap-2 text-xs text-3">
                            <span className="w-3">{star}</span>
                            <Star size={11} className="fill-amber-500 text-amber-500" />
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-hover">
                              <div className="h-full rounded-full bg-amber-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-6 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {reviews.reviews.length === 0 && (
                  <p className="mb-6 rounded-xl border border-line bg-hover p-5 text-sm text-3">
                    No written reviews yet — ratings shown include verified PAQVERO purchases.
                  </p>
                )}
                {reviews.mine && !editingReview && (
                  <div className="mb-4 rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5" data-testid="review-mine">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Stars value={reviews.mine.rating} />
                        <span className="text-sm font-bold text-1">{reviews.mine.title}</span>
                      </div>
                      <span data-testid="review-mine-status"
                        className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${reviews.mine.status === "pending" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-red-500/40 bg-red-500/10 text-red-400"}`}>
                        {reviews.mine.status === "pending" ? "Pending approval" : "Not approved"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-2">{reviews.mine.text}</p>
                    <div className="mt-2.5 flex items-center justify-between">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-500">Your review · Verified Buyer</p>
                      <button onClick={() => startEditReview(reviews.mine)} data-testid="review-edit-open"
                        className="text-[11px] font-bold text-amber-400 hover:text-amber-300">Edit review</button>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {reviews.reviews.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-line bg-surface p-5" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Stars value={r.rating} />
                          <span className="text-sm font-bold text-1">{r.title}</span>
                        </div>
                        <span className="text-[11px] text-3">{fmtDate(r.created_at)}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-2">{r.text}</p>
                      <div className="mt-2.5 flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-500">
                          {r.user_name} · Verified Buyer
                        </p>
                        {r.mine && (
                          <button onClick={() => startEditReview(r)} data-testid={`review-edit-${r.id}`}
                            className="text-[11px] font-bold normal-case tracking-normal text-amber-400 hover:text-amber-300">
                            Edit review
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {user && (canReview?.can_review || editingReview) && (
                  <form onSubmit={editingReview ? saveEditedReview : submitReview} className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-5" data-testid="review-form">
                    <div className="flex items-center justify-between">
                      <h4 className="font-display text-base font-medium">{editingReview ? "Edit your review" : "Write a review"}</h4>
                      {editingReview && (
                        <button type="button" onClick={() => setEditingReview(false)} data-testid="review-edit-cancel"
                          className="text-[11px] font-bold text-3 hoverable-t1">Cancel</button>
                      )}
                    </div>
                    <div className="mt-3 flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button key={i} type="button" data-testid={`review-star-${i}`} onClick={() => setReviewForm((f) => ({ ...f, rating: i }))}>
                          <Star size={22} className={i <= reviewForm.rating ? "fill-amber-500 text-amber-500" : "fill-neutral-800 text-neutral-800"} />
                        </button>
                      ))}
                    </div>
                    <input
                      data-testid="review-title"
                      value={reviewForm.title}
                      onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))}
                      required
                      placeholder="Review title"
                      className="mt-3 h-11 w-full rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                    />
                    <textarea
                      data-testid="review-text"
                      value={reviewForm.text}
                      onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))}
                      required
                      rows={3}
                      placeholder="How did this product hold up?"
                      className="mt-3 w-full rounded-xl border border-line bg-input px-4 py-3 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none"
                    />
                    <button
                      data-testid="review-submit"
                      type="submit"
                      disabled={reviewLoading}
                      className="mt-3 flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-60"
                    >
                      {reviewLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {editingReview ? "Save changes" : "Submit review"}
                    </button>
                    <p className="mt-2 text-[11px] text-3">Reviews appear on the product page after a quick moderation check.</p>
                  </form>
                )}
                {user && canReview && !canReview.can_review && canReview.reason === "not_purchased" && (
                  <p className="mt-6 text-xs text-3">Only verified buyers can review this product.</p>
                )}
                {!user && (
                  <button onClick={() => navigate("/login")} className="mt-6 text-xs font-bold text-amber-500 hover:text-amber-400" data-testid="review-signin-prompt">
                    Sign in to write a review
                  </button>
                )}
              </>
            )}
          </div>
          <RecentlyViewed excludeId={productId} />
        </div>
      </div>
    </div>
  );
}
